import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, Copy, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [resendReady, setResendReady] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      setResendReady(!!data.resend_configured);
      if (data.link) setDevLink(data.link);
      toast.success("If that email exists, a reset link is on the way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset link.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!devLink) return;
    try {
      await navigator.clipboard.writeText(devLink);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the link to copy.");
    }
  };

  return (
    <div className="container-narrow py-20 grid lg:grid-cols-2 gap-12 items-center" data-testid="forgot-password-page">
      <div className="hidden lg:block">
        <div className="label-eyebrow mb-3">Password reset</div>
        <h1 className="font-heading text-5xl font-semibold leading-tight mb-4">
          Everyone forgets. <span className="italic text-marigold">We remember.</span>
        </h1>
        <p className="text-ink2 max-w-md">
          Type your email. If we know you, a one-tap reset link lands in your inbox within a minute.
          Links expire in 30 minutes and only work once.
        </p>
      </div>

      <div className="card-soft p-8 max-w-md w-full mx-auto">
        {!sent && (
          <form onSubmit={submit} data-testid="forgot-form">
            <div className="w-11 h-11 rounded-2xl bg-marigold/15 text-marigold flex items-center justify-center mb-4">
              <Mail className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">Reset your password</h2>
            <p className="text-sm text-ink2 mb-6">
              We&apos;ll email a one-tap reset link. Check your inbox (and spam) within a minute.
            </p>
            <label className="text-sm font-medium block mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input-soft mb-4"
              placeholder="you@example.com"
              data-testid="forgot-email"
            />
            <button className="btn-primary w-full justify-center" disabled={busy} data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <div className="text-center mt-4">
              <Link to="/login" className="text-sm text-ink2 hover:text-marigold inline-flex items-center gap-1" data-testid="forgot-back-to-login">
                <ArrowLeft className="w-3 h-3" /> Back to log in
              </Link>
            </div>
          </form>
        )}

        {sent && (
          <div data-testid="forgot-sent-panel">
            <div className="w-11 h-11 rounded-2xl bg-teal/15 text-teal flex items-center justify-center mb-4">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">Check your inbox</h2>
            <p className="text-sm text-ink2 mb-4">
              If <span className="font-medium text-ink">{email}</span> matches an account, we&apos;ve sent a reset link that expires in 30 minutes.
            </p>

            {!resendReady && devLink && (
              <div className="rounded-xl border-2 border-dashed border-marigold/50 bg-marigold/5 p-4 mb-4" data-testid="forgot-dev-link">
                <div className="text-xs font-semibold uppercase tracking-wider text-marigold mb-1">Dev / preview mode</div>
                <div className="text-xs text-ink2 mb-2">
                  Email delivery isn&apos;t configured, so the reset link is here for you to copy:
                </div>
                <div className="flex items-center gap-2">
                  <input readOnly value={devLink} className="input-soft text-xs flex-1" data-testid="forgot-dev-link-input" />
                  <button onClick={copyLink} className="btn-outline-ink text-xs px-3 py-2" data-testid="forgot-dev-link-copy">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => { setSent(false); setDevLink(null); }} className="btn-outline-ink text-sm flex-1 justify-center" data-testid="forgot-send-again">
                Send again
              </button>
              <button onClick={() => nav("/login")} className="btn-ghost text-sm flex-1 justify-center" data-testid="forgot-return-login">
                Back to log in
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
